import { Component, ElementRef, Injector, OnInit, output, OnDestroy, Renderer2, HostListener, AfterViewInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, RouterLink, NavigationStart } from '@angular/router';
import { delay, takeUntil, filter, take, BehaviorSubject } from 'rxjs';
import { ChatUser } from 'src/app/shared/models/chatUser.model';
import { TBSBaseComponent } from 'src/app/shared/models/tbsBaseComponent';
import { GlobalObjectsService } from 'src/app/shared/services/global-objects.service';
import { ChatMessage } from 'src/app/shared/models/chatMessage.model';
import { CommonService } from 'src/app/core/services';
import { SharedModule } from 'src/app/shared/shared.module';
import { AiChatService } from 'src/app/modules/ai-chat/ai-chat.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { SessionStorageKey, NgxUiLoaderKey, AIInstruction } from 'src/app/shared/models/constants';
import { ChatTileComponent } from 'src/app/modules/ai-chat/chat-tile/chat-tile.component';
import { LanguageService } from 'src/app/core/services/language.service';
import { XssSanitizerService } from '../../../shared/services/xss-sanitizer.service';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-aon-ai-chat-panel',
  templateUrl: './aon-ai-chat-panel.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPopover, ReactiveFormsModule, SharedModule, ChatTileComponent, RouterLink]
})
export class AonAiChatPanelComponent extends TBSBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  public closed = output<void>();
  public currentDate = new Date();
  public chatForm = new FormGroup({
    queryInput: new FormControl('', [Validators.required, Validators.pattern(/\S/)])
  });
  public welcomeTitle = '';

  // hardcoded values should be removed when backend implementation is done
  private readonly currentUser: ChatUser = {
    name: '',
    avatarUrl: ''
  };

  private readonly aiAssistant: ChatUser = {
    name: 'Aon.AI.Chat.Panel.AssistantName',
    avatarUrl: ''
  };

  // collection of messages
  public messages: ChatMessage[] = [];
  private _waitingForResponse: boolean = false;
  private statusOverrideTimer: ReturnType<typeof setTimeout> | null = null;
  /** Pending `closed` emit after the closing animation — cleared on destroy so it can't fire from a destroyed component (NG0953). */
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  get waitingForResponse(): boolean { return this._waitingForResponse; }
  set waitingForResponse(value: boolean) {
    if (this._waitingForResponse === value) return;
    this._waitingForResponse = value;
    this.updateScreenReaderStatus(value);
  }
  private readonly resourceStringsLoaded$ = new BehaviorSubject<boolean>(false);

  backgroundColor: string;
  private documentScrollEventListener?: () => void;
  private isInputJustFocused: boolean = false;
  disclaimerText = '';
  @ViewChild('disclaimer') disclaimer: ElementRef;
  linkWidth: number = 100;
  @ViewChild('termsLink', { static: false }) termsLink!: ElementRef;
  @ViewChild('aiChatMessageAnnouncer', { static: false }) messageAnnouncer?: ElementRef;

  /** Element that had focus before the panel opened, for restore on close. */
  private previouslyFocused: HTMLElement | null = null;


  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(event: Event): void {
    event.preventDefault();
    this.close();
  }

  @HostListener('document:keydown.tab', ['$event'])
  @HostListener('document:keydown.shift.tab', ['$event'])
  onTabKey(event: Event): void {
    // Guard against Shift+Tab firing this handler twice.
    if (event.defaultPrevented) return;

    const panel = this.elementRef.nativeElement.querySelector('.ai-chat-panel') as HTMLElement | null;
    if (!panel) return;

    // Avatar close button lives outside the panel but belongs in the focus cycle.
    const avatarClose = document.getElementById('ai-chat-avatar-toggle');
    const elements = this.getPanelFocusableElements(panel);
    if (!elements.length) return;

    const first = elements[0];
    const last = elements[elements.length - 1];
    const active = document.activeElement as HTMLElement;
    const isForward = !(event as KeyboardEvent).shiftKey;
    const inPanel = elements.includes(active);
    const onAvatar = active === avatarClose;

    if (isForward) {
      if (active === last) {
        event.preventDefault();
        (avatarClose ?? first).focus();
      } else if (onAvatar || !inPanel) {
        event.preventDefault();
        first.focus();
      }
      // Otherwise let the browser move naturally within the panel
    } else if (active === first) {
      event.preventDefault();
      (avatarClose ?? last).focus();
    } else if (onAvatar || !inPanel) {
      event.preventDefault();
      last.focus();
    }
    // Otherwise let the browser move naturally within the panel
  }

  private getPanelFocusableElements(panel: HTMLElement): HTMLElement[] {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),'
      + ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [role="button"]:not([aria-disabled="true"])';
    return Array.from(panel.querySelectorAll<HTMLElement>(selector))
      .filter(el => this.helperService.isInViewport(el));
  }

  constructor(
    injector: Injector,
    private readonly globalObjectsService: GlobalObjectsService,
    private readonly elementRef: ElementRef,
    private readonly commonService: CommonService,
    private readonly chatService: AiChatService,
    private readonly renderer: Renderer2,
    private readonly router: Router,
    private readonly ngxLoader: NgxUiLoaderService,
    private readonly languageService: LanguageService,
    private readonly xssSanitizer: XssSanitizerService
  ) {
    super(injector);
    this.addResourceStringKeys([
      'Aon.AI.Widget.AskAnything',
      'Aon.AI.Widget.SubmitQuestion.AccessibleDescription',
      'Aon.AI.Widget.SubmitQuestion.AccessibleLabel',
      'Aon.AiChat.Panel.WelcomeTitle',
      'Aon.AiChat.Panel.WelcomeSubTitle',
      'Aon.AI.Chat.Panel.ErrorMessage',
      'Aon.AI.Widget.AccessibleSendMessage',
      'Aon.AI.Avatar.AonAiChat',
      'Aon.AI.Avatar.CloseChat',
      'Aon.AI.Chat.LiveChatPrompt.Message',
      'Aon.AI.Chat.Panel.DisclaimerText',
      'Aon.AI.Chat.Panel.DisclaimerAONAILink',
      'Aon.AI.Chat.Panel.Thinking',
      'Aon.AI.Chat.Panel.Ready',
      'Aon.Ai.ChatTileFeedback',
      'Aon.AI.Chat.SendingMessage',
      'Aon.AI.Chat.MessageSent',
      'Aon.AI.Chat.MessageFrom'
    ]);
  }

  ngOnInit(): void {
    this.previouslyFocused = document.activeElement as HTMLElement | null;

    this.backgroundColor = this.getBackgroundColor();
    // read messages from session storage 'ai-chat-messages'
    const storedMessages = sessionStorage.getItem(SessionStorageKey.AiChatMessages);
    if (storedMessages) {
      this.messages = JSON.parse(storedMessages);
    } else {
      this.messages = [];
    }

    this.waitingForResponse = this.messages.some(msg => msg.thinkingIndicator === true);

    // Scenario: page was reloaded while an API call was in-flight — the HTTP connection is lost.
    // Detect the orphaned thinking indicator and re-send the original query.
    const pendingMessage = this.messages.find(msg => msg.thinkingIndicator === true);
    if (pendingMessage && !this.chatService.isRequestInFlight(pendingMessage.questionID)) {
      const senderMessage = this.messages.find(msg => msg.type === 'sender' && msg.questionID === pendingMessage.questionID);
      const pendingQuery = senderMessage?.message;
      if (pendingQuery) {
        this.sendQueryToAI(pendingQuery, pendingMessage.questionID);
      }
    }

    // Keep the currently open panel in sync when a stale component instance updates sessionStorage.
    this.chatService.messagesUpdated$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        const latest = sessionStorage.getItem(SessionStorageKey.AiChatMessages);
        this.messages = latest ? JSON.parse(latest) : [];
        this.waitingForResponse = this.messages.some(msg => msg.thinkingIndicator === true);
      });

    this.currentUser.name = this.globalObjectsService.compactEmployeeInformation.value?.preferredName;
    this.loadResourceStringAsObservable()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(res => {
        this.getResouceStringsAsObject(res);
        this.resourceStringsLoaded$.next(true);
        this.disclaimerText = this.resourceStrings['Aon.AI.Chat.Panel.DisclaimerText'];
        // replace #name in resource string welcome title
        this.welcomeTitle = this.resourceStrings['Aon.AiChat.Panel.WelcomeTitle'].replace(/#name/g, this.currentUser.name);
        this.helperService.appendAiElement(this.elementRef, this.renderer);
      });

    // Subscribe to Nice chat window state
    this.globalObjectsService.niceChatWindowState
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(state => {
        if (state === 'open-requested') {
          // Show full-screen loader and close AI panel immediately
          // Loader will be stopped by GlobalObjectsService when state becomes 'open' or 'idle'
          this.ngxLoader.start(NgxUiLoaderKey.LIVE_CHAT);
          this.close();
        }
      });

    // Close chat panel on route navigation
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntil(this.unsubscribe)
      )
      .subscribe(() => {
        this.close();
      });

    // Prevent body scroll when chat panel is open
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
  }

  ngAfterViewInit(): void {
    // Blur input on document scroll
    // Fix given for Mobile IOS Safari Device where body background scroll was happening in the background
    this.documentScrollEventListener = this.renderer.listen('document', 'scroll', () => {
      this.blurInput();
    });
    this.calculateLinkWidth();
  }

  ngOnDestroy(): void {
    this.documentScrollEventListener?.();
    this.messages = []; // Complete the closed event emitter
    // Restore body scroll when chat panel is destroyed
    this.renderer.removeStyle(document.body, 'overflow');

    if (this.statusOverrideTimer) {
      clearTimeout(this.statusOverrideTimer);
      this.statusOverrideTimer = null;
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.globalObjectsService.w3ScreenReader.overrideMessage = '';
    this.globalObjectsService.w3ScreenReader.suppressPoliteAnnouncements = false;
    this.globalObjectsService.w3ScreenReader.pageLoadingAnnouncement = false;
    this.globalObjectsService.w3ScreenReader.pageLoadedAnnouncement = false;

    // queueMicrotask defers focus restore past Angular's teardown.
    const target = this.previouslyFocused;
    queueMicrotask(() => target?.focus?.());

    this.signalUnsubscribe();
  }

  private updateScreenReaderStatus(thinking: boolean): void {
    if (this.statusOverrideTimer) {
      clearTimeout(this.statusOverrideTimer);
      this.statusOverrideTimer = null;
    }
    if (thinking) {
      this.globalObjectsService.w3ScreenReader.suppressPoliteAnnouncements = true;
      this.globalObjectsService.w3ScreenReader.pageLoadingAnnouncement = false;
      this.globalObjectsService.w3ScreenReader.pageLoadedAnnouncement = false;

      // 200ms delay lets focus and placeholder settle before the assertive live region fires.
      this.statusOverrideTimer = setTimeout(() => {
        this.statusOverrideTimer = null;
        this.globalObjectsService.w3ScreenReader.overrideMessage =
          this.resourceStrings['Aon.AI.Chat.Panel.Thinking'] || 'AI thinking';
      }, 200);
    }
  }

  private announceMessageToScreenReader(isSent: boolean, messageContent?: string): void {
    if (!this.messageAnnouncer) return;

    let announcement = '';
    if (isSent) {
      announcement = this.resourceStrings['Aon.AI.Chat.MessageSent'];
    } else {
      const messageFromLabel = this.resourceStrings['Aon.AI.Chat.MessageFrom'];
      announcement = `${messageFromLabel} ${this.aiAssistant.name}`;
    }
    this.messageAnnouncer.nativeElement.textContent = announcement;
  }

  close(): void {
    const panelElement = this.elementRef.nativeElement.querySelector('.ai-chat-panel');
    if (panelElement) {
      panelElement.classList.add('closing');
      // Wait for animation
      if (this.closeTimer) clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        this.closeTimer = null;
        this.closed.emit();
      }, 500);
    } else {
      this.closed.emit();
    }

    this.chatService.closeChat();
  }

  // Simplified scroll functions
  scrollToBottom(): void {
    setTimeout(() => {
      const content = this.elementRef.nativeElement.querySelector('.content');
      content?.scrollTo({ top: content.scrollHeight, behavior: 'smooth' });
    }, 100);
  }

  scrollWithAnimation(): void {
    const contentElement = this.elementRef.nativeElement.querySelector('.content');
    if (!contentElement) return;

    contentElement.scrollTo({ top: 0, behavior: 'instant' });
    const startTime = performance.now();
    const duration = 800;

    const animateScroll = (currentTime: number) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easing = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      contentElement.scrollTo({ top: contentElement.scrollHeight * easing, behavior: 'instant' });
      if (progress < 1) requestAnimationFrame(animateScroll);
    };

    requestAnimationFrame(animateScroll);
  }

  focusInput(): void {
    this.elementRef.nativeElement.querySelector('input')?.focus();
  }

  blurInput(): void {
    // Don't blur if input was just focused (scroll from clicking)
    if (this.isInputJustFocused) {
      return;
    }
    const input = this.elementRef.nativeElement.querySelector('input');
    if (document.activeElement === input) {
      input?.blur();
    }
  }

  onInputFocus(): void {
    // Set flag to ignore immediate scroll from clicking/focusing
    this.isInputJustFocused = true;
    // Clear flag after short delay - any scroll after this will blur the input
    setTimeout(() => {
      this.isInputJustFocused = false;
    }, 500);
  }

  setInitialQuery(query: string): void {
    if (query) {
      this.globalObjectsService.w3ScreenReader.suppressPoliteAnnouncements = true;
    }

    this.resourceStringsLoaded$
      .pipe(filter(loaded => loaded), take(1), takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.chatForm.get('queryInput')?.setValue(query);
        this.onSubmit();
      });
  }

  private set message(params: {
    type: 'sender' | 'receiver',
    questionID: string,
    message: string,
    thinkingIndicator?: boolean,
    isRequestHasError?: boolean,
    hasLiveChatButton?: boolean
  }) {
    this.messages.push({
      type: params.type,
      questionID: params.questionID,
      message: params.message,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      user: params.type === 'sender' ? this.currentUser : this.aiAssistant,
      withAnimation: true,
      isRequestHasError: params.isRequestHasError || false,
      thinkingIndicator: params.thinkingIndicator || false,
      hasLiveChatButton: params.hasLiveChatButton || false
    });
  }

  private cacheMessagesToSession(): void {
    sessionStorage.setItem(SessionStorageKey.AiChatMessages, JSON.stringify(this.messages));
    this.scrollToBottom();
  }

  private updateAIResponseMessage(params: {
    message?: string,
    isRequestHasError?: boolean,
    hasLiveChatButton?: boolean
  }) {
    if (this.messages[this.messages.length - 1]?.hasLiveChatButton) {
      return; // Prevent adding multiple live chat prompts
    }

    // Find the message with thinking indicator
    const messageIndex = this.messages.findIndex(msg => msg.thinkingIndicator === true);

    if (messageIndex === -1) {
      // Component was destroyed (panel closed) before response arrived — update sessionStorage directly
      const raw = sessionStorage.getItem(SessionStorageKey.AiChatMessages);
      if (!raw) return;
      const stored: ChatMessage[] = JSON.parse(raw);
      const storedIndex = stored.findIndex(msg => msg.thinkingIndicator === true);
      if (storedIndex === -1) return;
      const storedThinking = stored[storedIndex];
      storedThinking.timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      storedThinking.thinkingIndicator = false;
      storedThinking.message = params.isRequestHasError ? this.resourceStrings['Aon.AI.Chat.Panel.ErrorMessage'] : params.message;
      storedThinking.isRequestHasError = params.isRequestHasError || false;
      storedThinking.hasLiveChatButton = params.hasLiveChatButton || false;
      stored.splice(storedIndex, 1);
      stored.push(storedThinking);
      sessionStorage.setItem(SessionStorageKey.AiChatMessages, JSON.stringify(stored));
      this.chatService.notifyMessagesUpdated();
      return;
    }

    const thinkingMessage = this.messages[messageIndex];

    // Update property values
    thinkingMessage.thinkingIndicator = false;
    thinkingMessage.message = params.isRequestHasError ? this.resourceStrings['Aon.AI.Chat.Panel.ErrorMessage'] : params.message;
    thinkingMessage.isRequestHasError = params.isRequestHasError || false;
    thinkingMessage.hasLiveChatButton = params.hasLiveChatButton || false;

    this.messages.splice(messageIndex, 1);

    // Announce AI response to screen reader BEFORE UI update
    if (!params.isRequestHasError) {
      this.announceMessageToScreenReader(false);
    }

    // Re-add updated message to trigger animation (after announcement)
    this.message = thinkingMessage;

    if (this.statusOverrideTimer) {
      clearTimeout(this.statusOverrideTimer);
    }
    const feedbackPrompt = !thinkingMessage.isRequestHasError && !thinkingMessage.hasLiveChatButton
      ? (this.resourceStrings['Aon.Ai.ChatTileFeedback'] || '')
      : '';

    // Delay w3ScreenReader announcement so "Message from" comes first
    setTimeout(() => {
      this.globalObjectsService.w3ScreenReader.overrideMessage = feedbackPrompt
        ? `${thinkingMessage.message} ${feedbackPrompt}`
        : thinkingMessage.message;
    }, 30);
    this.statusOverrideTimer = setTimeout(() => {
      this.globalObjectsService.w3ScreenReader.overrideMessage = '';
      this.globalObjectsService.w3ScreenReader.suppressPoliteAnnouncements = false;
      this.globalObjectsService.w3ScreenReader.pageLoadingAnnouncement = false;
      this.globalObjectsService.w3ScreenReader.pageLoadedAnnouncement = false;
      this.statusOverrideTimer = null;
    }, 10000);

    // Cache Message Logs in the session storage
    this.cacheMessagesToSession();
  }

  submitQuery(): void {
    let query = this.chatForm.get('queryInput')?.value?.trim();
    query = this.xssSanitizer.sanitizeContent(query);
    if (!query) return;

    // Announce "Sending message" immediately
    if (this.messageAnnouncer) {
      this.messageAnnouncer.nativeElement.textContent = this.resourceStrings['Aon.AI.Chat.SendingMessage'];
    }

    this.blurInput()

    // Clear input
    this.chatForm.get('queryInput')?.setValue('');

    const questionID = this.helperService.randomGuid();

    // Add user message
    this.message = {
      type: 'sender',
      questionID,
      message: query
    };

    // Announce "Message Sent" after brief delay (so "Sending message" is heard first)
    setTimeout(() => {
      this.announceMessageToScreenReader(true);
    }, 300);

    this.waitingForResponse = true;

    // Add thinking message
    this.message = {
      type: 'receiver',
      questionID,
      message: '',
      thinkingIndicator: true
    };

    // Persist thinking state immediately so it survives panel close or page reload
    sessionStorage.setItem(SessionStorageKey.AiChatMessages, JSON.stringify(this.messages));

    this.scrollToBottom();

    this.sendQueryToAI(query, questionID);
  }

  private sendQueryToAI(query: string, questionID: string): void {
    this.waitingForResponse = true;
    this.chatService.markRequestInFlight(questionID);
    this.chatService.sendMessage(query, this.languageService.selectedLanguage?.cultureCode, this.commonService.siteName, questionID)
      .pipe(delay(1500))
      .subscribe({
        next: (response) => {
          this.chatService.clearRequestInFlight(questionID);
          this.waitingForResponse = false;
          const responseMessageForDisplay = this.getResponseMessageForDisplay(response?.response?.responseText, response?.response?.instruction);
          this.updateAIResponseMessage({
            message: responseMessageForDisplay,
            isRequestHasError: !response.result,
            hasLiveChatButton: response?.response?.instruction == AIInstruction.StartLiveChat
          });
        },
        error: () => {
          this.chatService.clearRequestInFlight(questionID);
          this.waitingForResponse = false;
          this.updateAIResponseMessage({ isRequestHasError: true });
        }
      });
  }

  public getResponseMessageForDisplay(responseText: string, instruction: string): string {
    const responseMessage = instruction === AIInstruction.StartLiveChat
      ? (responseText || this.resourceStrings['Aon.AI.Chat.LiveChatPrompt.Message'])
      : responseText;
    return this.normalizeAiResponseExternalLinks(responseMessage);
  }

  /**
   * Ensures external links in AI response HTML open in a new tab while preserving
   * existing behavior for same-domain and relative links.
   */
  private normalizeAiResponseExternalLinks(responseHtml: string): string {
    const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';
    
    if (!responseHtml || typeof document === 'undefined') {
      return responseHtml;
    }

    const normalizedCurrentHost = (currentHostname || '').trim().toLowerCase();
    if (!normalizedCurrentHost) {
      return responseHtml;
    }

    const template = document.createElement('template');
    template.innerHTML = responseHtml;

    const anchors = Array.from(template.content.querySelectorAll('a[href]'));
    anchors.forEach(anchor => {
      const rawHref = (anchor.getAttribute('href') || '').trim();
      if (!/^https?:\/\//i.test(rawHref)) {
        return;
      }

      try {
        const parsedUrl = new URL(rawHref);
        if (parsedUrl.hostname.toLowerCase() === normalizedCurrentHost) {
          return;
        }

        anchor.setAttribute('target', '_blank');

        const existingRel = (anchor.getAttribute('rel') || '').trim();
        const relTokens = existingRel ? existingRel.split(/\s+/) : [];
        const relTokenSet = new Set(relTokens.map(token => token.toLowerCase()));

        if (!relTokenSet.has('noopener')) {
          relTokens.push('noopener');
        }
        if (!relTokenSet.has('noreferrer')) {
          relTokens.push('noreferrer');
        }

        anchor.setAttribute('rel', relTokens.join(' ').trim());
      } catch {
        // Ignore malformed URLs and preserve original markup.
      }
    });

    return template.innerHTML;
  }

  public get disableSubmitButton(): boolean {
    return this.waitingForResponse || (this.chatForm?.invalid ?? false);
  }

  getBackgroundColor() {
    let uiConfigType = this.uiconfigService.getUIConfiguration();
    const clientBrandingColour = uiConfigType?.uiConfiguration?.clientPrimaryBrandColour;
    let reg = /^#([0-9a-f]{3}){1,2}$/i;
    if (reg.test(clientBrandingColour)) {
      return clientBrandingColour;
    }
    return null;
  }

  private addLiveChatPromptMessage() {
    const questionID = this.helperService.randomGuid();
    const userQuery = this.chatForm.get('queryInput')?.value?.trim();

    // Clear input
    this.chatForm.get('queryInput')?.setValue('');

    // Add user message requesting live chat
    this.message = {
      type: 'sender',
      questionID,
      message: userQuery
    };

    // Add agent response with live chat button using shared method
    this.addLiveChatAgentMessage(questionID);
  }

  private addLiveChatAgentMessage(questionID = this.helperService.randomGuid()): void {
    if (this.messages[this.messages.length - 1]?.hasLiveChatButton) {
      return; // Prevent adding multiple live chat prompts
    }

    // Wait for resource strings to be loaded (no duplicate API call)
    if (!this.resourceStringsLoaded$.value) {
      this.ngxLoader.start('resource-strings-loader');
    }

    this.resourceStringsLoaded$
      .pipe(
        filter(loaded => loaded === true),
        take(1),
        takeUntil(this.unsubscribe)
      )
      .subscribe(() => {
        this.ngxLoader.stop('resource-strings-loader');
        this.message = {
          type: 'receiver',
          questionID,
          message: this.resourceStrings['Aon.AI.Chat.LiveChatPrompt.Message'],
          hasLiveChatButton: true
        };

        // Cache Message Logs in the session storage
        this.cacheMessagesToSession();
      });
  }

  onSendAttempt(): void {
    if (this.waitingForResponse) {
      return; // silent no-op while AI is responding — nothing useful to redirect to
    }
    if (this.chatForm.invalid) {
      this.focusInput(); // redirect keyboard users back to the input
      return;
    }
    this.onSubmit();
  }

  onSubmit() {
    if (!this.disableSubmitButton) {
      this.submitQuery();
    }
  }

  public openLiveChat(): void {
    if (!this.isNiceChatEnabled) {
      return;
    }

    // Request live chat opening - nic-chat component will handle the request
    this.globalObjectsService.niceChatWindowState.next('open-requested');
  }

  public get isNiceChatEnabled(): boolean {
    return this.globalObjectsService.isNiceChatEnabled.value;
  }

  public onInputBlur(): void {
    if (this.chatForm.get('queryInput')?.invalid && this.chatForm.get('queryInput')?.value?.trim() === '') {
      this.chatForm.controls.queryInput.markAsUntouched();
    }
  }

  public navigateToHelp(): void {
    this.close();
    this.router.navigate(['/home/help']);
  }

  public onShowLiveChatPrompt(questionID: string): void {
    // Add agent response with live chat button after thumbs down
    this.addLiveChatAgentMessage();
  }

  get hasEllipsis(): boolean {
    return !!this.disclaimer && this.disclaimer.nativeElement.scrollWidth > this.disclaimer.nativeElement.clientWidth;
  }

  private calculateLinkWidth(): void {
    if (this.termsLink && this.disclaimer) {
      this.linkWidth = this.termsLink.nativeElement.offsetWidth;
      // Set max-width directly on the disclaimer element
      this.renderer.setStyle(this.disclaimer.nativeElement, 'max-width', `calc(100% - ${this.linkWidth}px)`);
    }
  }

}
